// src/components/DeviceLogs/index.js
import React, { useState } from 'react';
import { Card } from 'components/shadcn/ui/card';
import { HGTable } from '../HGTable';
import { useSearch, usePage, useSort } from '../../components/HGTable/hooks';
import { columns } from './constants';
import { useDeviceLogs } from './service';
import DeviceLogsFilters from './DeviceLogsFilters';

const getDefaultStartTime = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 16);
};

const getDefaultEndTime = () => new Date().toISOString().slice(0, 16);

export const DeviceLogs = () => {
    const [sortField, setSortField] = useState('ts');
    const [sortDirection, setSortDirection] = useState('descending');
    const [filterBy, setFilterBy] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');

    const [startTimeInput, setStartTimeInput] = useState(getDefaultStartTime);
    const [endTimeInput, setEndTimeInput] = useState(getDefaultEndTime);
    const [filterStartTime, setFilterStartTime] = useState(getDefaultStartTime);
    const [filterEndTime, setFilterEndTime] = useState(getDefaultEndTime);

    const [selectedLevels, setSelectedLevels] = useState([]);
    const [filterLevels, setFilterLevels] = useState([]);
    const [filterError, setFilterError] = useState('');

    const {
        data,
        isLoading,
        error: queryError,
    } = useDeviceLogs(filterStartTime, filterEndTime, filterLevels);

    const logs = data?.logs ?? [];
    const total = data?.total ?? 0;
    const capped = data?.capped ?? false;

    const filteredLogs = useSearch(logs, search, filterBy) ?? [];
    const sortedData = useSort(
        filteredLogs,
        sortField,
        sortDirection,
        sortField === 'ts' ? 'ts' : null
    );
    const logsToShow = usePage(sortedData, currentPage, 10);

    const handleApplyFilters = () => {
        if (
            startTimeInput &&
            endTimeInput &&
            new Date(startTimeInput) > new Date(endTimeInput)
        ) {
            setFilterError('End Time cannot be earlier than Start Time.');
            return;
        }
        setFilterError('');
        setFilterStartTime(startTimeInput);
        setFilterEndTime(endTimeInput);
        setFilterLevels(selectedLevels);
        setCurrentPage(1);
    };

    const isApplyDisabled = !startTimeInput || !endTimeInput;

    const toggleLevel = (level) => {
        setSelectedLevels((prev) =>
            prev.includes(level)
                ? prev.filter((l) => l !== level)
                : [...prev, level]
        );
    };

    return (
        <Card className="mt-4">
            <DeviceLogsFilters
                startTimeInput={startTimeInput}
                setStartTimeInput={setStartTimeInput}
                endTimeInput={endTimeInput}
                setEndTimeInput={setEndTimeInput}
                selectedLevels={selectedLevels}
                toggleLevel={toggleLevel}
                onApply={handleApplyFilters}
                isLoading={isLoading}
                isApplyDisabled={isApplyDisabled}
            />
            {(filterError || queryError) && (
                <p className="mx-5 mb-2 rounded bg-error px-3 py-2 text-sm text-error-foreground">
                    {filterError ||
                        queryError?.message ||
                        'An unknown error occurred'}
                </p>
            )}
            {!isLoading && !filterError && !queryError && total > 0 && (
                <p className={`mx-5 mb-2 rounded px-3 py-2 text-sm ${capped ? 'bg-warning text-warning-foreground' : 'text-muted-foreground'}`}>
                    {capped
                        ? `Showing the most recent ${total} results — narrow your time range to see more specific results.`
                        : `Showing ${total} result${total !== 1 ? 's' : ''}`}
                </p>
            )}
            <HGTable
                title="Alerts"
                data={logsToShow}
                columns={columns}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                filterBy={filterBy}
                setFilterBy={setFilterBy}
                sortField={sortField}
                setSortField={setSortField}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                loading={isLoading}
                search={search}
                setSearch={setSearch}
                canFilter={true}
                canSearch={true}
                pageSize={10}
                totalItems={filteredLogs.length}
                onSearchChange={setSearch}
            />
        </Card>
    );
};

export default DeviceLogs;
